import { AxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { QuestionsResponse } from '@/types/module'
import {
  CaseStudyListResponse,
  CaseStudyParams,
  CaseStudyQuestionListResponse,
  QuestionListBody,
  QuestionParams,
  QuestionResponse,
} from '@/types/question'
import { axiosInstance } from '@/lib/axios'
import { TypeValue } from '@/features/question-lib/constant'
import BaseService from './baseService'

interface reqBody {
  module_id: number
  chapter_id: string
  query: string
  type?: string
  section?: string
  case_study?: boolean
  page: number
}

export const getQuestions = async (data: reqBody) => {
  const params = {
    module_id: data.module_id,

    query: data.query,
    ...(data.chapter_id !== 'all'
      ? { chapter_ids: [Number(data.chapter_id)] }
      : {}),
    ...(data.type && data.type !== TypeValue.all ? { type: data.type } : {}),
    ...(data.section && data.section !== TypeValue.all
      ? { section: data.section }
      : {}),
  }
  const response = await axiosInstance.post<QuestionsResponse>(
    `/question/module?page=${data.page}&size=10`,
    params
  )
  return response.data
}

export const useGetQuesitons = (data: reqBody, enabled: boolean = true) => {
  const {
    data: dataRes,
    isLoading,
    error,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [
      'getQuestions',
      data.chapter_id,
      data.type,
      data.query,
      data.module_id,
      data.page,
      data.section,
      data.case_study,
      data.section,
      data,
    ],
    queryFn: () => getQuestions(data),
    enabled: enabled && !!(data.module_id && data.chapter_id),
  })

  return {
    questions: dataRes?.data,
    meta: dataRes?.meta,
    isLoadingQuestions: isLoading || isRefetching,
    error,
    refetchQuestions: refetch,
  }
}

class QuestionService extends BaseService {
  async getQuestions(
    params: QuestionParams,
    body: QuestionListBody
  ): Promise<QuestionResponse> {
    try {
      const response = await this.post<QuestionResponse>(
        `/question/module`,
        body,
        false,
        params
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch questions'
        )
      }
      throw error
    }
  }

  async getCaseStudies(
    params: CaseStudyParams
  ): Promise<CaseStudyListResponse> {
    try {
      const response = await this.get<CaseStudyListResponse>(
        `/question/case-studies/module/${params.module_id}`,
        params
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch case studies'
        )
      }
      throw error
    }
  }

  async getQuestionByCaseStudy(
    case_study_id: number
  ): Promise<CaseStudyQuestionListResponse> {
    try {
      const response = await this.get<CaseStudyQuestionListResponse>(
        `/question/case-study/${case_study_id}/questions`
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message ||
            'Failed to fetch question by case study'
        )
      }
      throw error
    }
  }
}

export const questionService = new QuestionService()
